import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { ThemeProvider } from "@/components/theme-provider";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { useAuth } from "@/hooks/useAuth";
import AuthInitializer from "@/components/auth/AuthInitializer";
import StartupMessage from "@/components/StartupMessage";
import LoginForm from "./components/auth/LoginForm";
import DashboardLayout from "./components/layout/DashboardLayout";
import Dashboard from "./pages/Dashboard";
import Users from "./pages/Users";
import Companies from "./pages/Companies";
import Chat from "./pages/Chat";
import Documentos from "./pages/Documentos";
import SystemSettings from "./pages/SystemSettings";
import Campaigns from "./pages/Campaigns";
import Templates from "./pages/Templates";
import NotFound from "./pages/NotFound";
import ForgotPasswordPage from "./pages/ForgotPasswordPage";
import ResetPasswordPage from "./pages/ResetPasswordPage";
import ForceChangePassword from "./pages/ForceChangePassword";
import Leads from "./pages/Leads";
import { WebSocketProvider } from "./contexts/WebSocketContext";
import About from "@/pages/About";

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: (failureCount, error) => {
        // Não tentar novamente se for erro 401 (não autorizado)
        if ((error as any)?.response?.status === 401) {
          return false;
        }
        return failureCount < 2;
      },
      refetchOnWindowFocus: false,
      staleTime: 5 * 60 * 1000, // 5 minutos
    },
  },
});

const AppRoutes = () => {
  const { isAuthenticated, logout, isCheckingAuth } = useAuth();

  // Não renderizar nada durante verificação inicial
  if (isCheckingAuth) {
    return null;
  }

  return (
    <Routes>
      {/* Rotas públicas */}
      <Route
        path="/login"
        element={
          !isAuthenticated ? (
            <LoginForm />
          ) : (
            <Navigate to="/dashboard" replace />
          )
        }
      />
      <Route path="/forgot-password" element={<ForgotPasswordPage />} />
      <Route path="/reset-password" element={<ResetPasswordPage />} />
      <Route path="/change-password" element={<ForceChangePassword />} />

      {/* Rotas protegidas */}
      <Route
        path="/"
        element={
          isAuthenticated ? (
            <DashboardLayout onLogout={logout} />
          ) : (
            <Navigate to="/login" replace />
          )
        }
      >
        <Route index element={<Navigate to="/dashboard" replace />} />
        <Route path="dashboard" element={<Dashboard />} />
        <Route path="usuarios" element={<Users />} />
        <Route path="empresas" element={<Companies />} />
        <Route path="chat" element={<Chat />} />
        <Route path="documentos" element={<Documentos />} />
        <Route path="campanhas" element={<Campaigns />} />
        <Route path="leads" element={<Leads />} />
        <Route path="templates" element={<Templates />} />
        <Route path="configuracoes" element={<SystemSettings />} />
        <Route path="about" element={<About />} />
        <Route path="*" element={<NotFound />} />
      </Route>

      {/* Fallback */}
      <Route
        path="*"
        element={
          <Navigate to={isAuthenticated ? "/dashboard" : "/login"} replace />
        }
      />
    </Routes>
  );
};

// Novo componente para encapsular a lógica de autenticação + WebSocketProvider
function AppRoutesWithWebSocket() {
  const { isAuthenticated } = useAuth();
  return isAuthenticated ? (
    <WebSocketProvider>
      <AppRoutes />
    </WebSocketProvider>
  ) : (
    <AppRoutes />
  );
}

const App = () => {
  return (
    <ThemeProvider defaultTheme="light" storageKey="vite-ui-theme">
      <QueryClientProvider client={queryClient}>
        <TooltipProvider>
          <AuthInitializer>
            <BrowserRouter>
              <Toaster />
              <Sonner />
              <StartupMessage />
              <AppRoutesWithWebSocket />
            </BrowserRouter>
          </AuthInitializer>
        </TooltipProvider>
      </QueryClientProvider>
    </ThemeProvider>
  );
};

export default App;
